import { useNavigate, useLocation } from 'react-router'
import { useLocalStorage } from '~/shared/hooks/useLocalStorage'
import { PRODUCT_OPTIONS, type Product } from './MembersDashboardService'

const PRODUCT_SCOPE_KEY = 'membersDashboard.productScope'
const CONTRACT_SCOPE_KEY = 'membersDashboard.contract'

/** Parse a `?product=` value back to a Product, defaulting to `all` when absent/unknown. */
const parseProduct = (raw: string | null): Product =>
  PRODUCT_OPTIONS.some(o => o.value === raw) ? (raw as Product) : 'all'

/** Parse a `?contract=` value back to a positive contract id, or null (all contracts). */
const parseContractId = (raw: string | null): number | null => {
  const id = raw == null ? NaN : Number(raw)
  return Number.isInteger(id) && id > 0 ? id : null
}

// Query string for a given scope: product only when not the default `all`, contract
// only when a specific one is selected — so default URLs stay clean.
const scopeQuery = (product: Product, contract: number | null): string => {
  const params = new URLSearchParams()
  if (product !== 'all') params.set('product', product)
  if (contract != null) params.set('contract', String(contract))
  return params.toString()
}

/** URL into a queue drill-down, carrying the current product + contract scope. */
export const queuePath = (queueKey: string, product: Product, contract: number | null): string => {
  const query = scopeQuery(product, contract)
  return query ? `/members/queues/${queueKey}?${query}` : `/members/queues/${queueKey}`
}

/** Overview URL carrying the scope, so returning from a queue keeps the filters. */
export const overviewPath = (product: Product, contract: number | null): string => {
  const query = scopeQuery(product, contract)
  return query ? `/members?${query}` : '/members'
}

interface DashboardScope {
  product: Product
  contract: number | null
  setProduct: (next: Product) => void
  setContract: (next: number | null) => void
}

/**
 * Product + contract scope for the dashboard. The URL (`?product=`, `?contract=`) is
 * the source of truth — so scopes persist across the overview ↔ queue drill-down
 * navigation and survive refresh/back — backed by `localStorage` so they're also
 * remembered when re-entering without params (e.g. via the nav). Mirrors how Inbox
 * persists its filters (URL + localStorage). Defaults: product `all`, contract none.
 */
export const useDashboardScope = (): DashboardScope => {
  const navigate = useNavigate()
  const location = useLocation()
  const [storedProduct, setStoredProduct] = useLocalStorage(PRODUCT_SCOPE_KEY, 'all')
  const [storedContract, setStoredContract] = useLocalStorage(CONTRACT_SCOPE_KEY)
  const params = new URLSearchParams(location.search)
  const product = parseProduct(params.get('product') ?? storedProduct)
  const contract = parseContractId(params.get('contract') ?? storedContract)

  // Rewrite both scopes together so each setter preserves the other, updating the URL
  // (source of truth) and the localStorage mirror in one step.
  const replaceScope = (nextProduct: Product, nextContract: number | null) => {
    setStoredProduct(nextProduct)
    setStoredContract(nextContract == null ? null : String(nextContract))
    const next = new URLSearchParams(location.search)
    if (nextProduct === 'all') next.delete('product')
    else next.set('product', nextProduct)
    if (nextContract == null) next.delete('contract')
    else next.set('contract', String(nextContract))
    void navigate({ pathname: location.pathname, search: next.toString() }, { replace: true })
  }

  return {
    product,
    contract,
    setProduct: next => replaceScope(next, contract),
    setContract: next => replaceScope(product, next),
  }
}
